import { createContext, FC, useContext, useEffect, useReducer, useRef } from 'react';
import localForage from 'localforage';
import API from 'services/AxiosConfig';
import { CompanyStateI, CompanyActionTypes, companyInitialState } from 'store';
import { companyReducer } from 'store/company/reducers';

const LEGACY_STATE_LS_KEY = 'company';
const COMPANY_CACHE_PREFIX = 'company';
const PERSIST_DEBOUNCE_MS = 300;
const COMPANY_STATE_KEYS: Array<keyof CompanyStateI> = [
  'locations',
  'users',
  'clients',
  'carePartners',
  'careManagerActivities',
  'applicants',
  'awardTypes',
  'deactivationReasons',
  'lostClientReasons',
  'prospectlostClientReason',
  'applicantStages',
  'referralPartner',
  'companies',
  'prospect',
  'milestones',
  'ratings',
  'companyRatings',
  'priorityIndexes',
  'priorityIndexesCP',
  'activityTypes',
  'companyTypes',
  'applicantsActivity',
  'applicantSources',
  'leadSources',
  'referralActivityTypes',
  'applicantActivityTypeCategory',
  'activityCategoryManagement',
  'prospectActivityCategory',
  'referralPartnersActivityCategory',
  'authorizationStages',
  'reAuthorizationStages',
  'authorizationTypes',
  'authorizations',
  'requestedStages',
  'tags',
  'tagCategory',
  'prospectStages',
  'contactTypes',
  'contactRelationships',
  'disciplinarySteps',
  'disciplinaryCategories',
  'disciplinaryAcks',
  'disciplinaryAttendanceTypes',
  'applicantThreshold'
] as const;

type CompanyStateKey = typeof COMPANY_STATE_KEYS[number];

const getCompanySliceStorageKey = (key: CompanyStateKey) => `${COMPANY_CACHE_PREFIX}:${key}`;

const buildStateFromSlices = (slices: Partial<CompanyStateI>) => ({
  ...companyInitialState,
  ...slices
});

const logCompanyCacheError = (action: string, error: unknown) => {
  console.error(`[Company cache] Failed to ${action}.`, error);
};

const removeCompanyCacheKeys = async () => {
  await Promise.all([
    localForage.removeItem(LEGACY_STATE_LS_KEY),
    ...COMPANY_STATE_KEYS.map((key) => localForage.removeItem(getCompanySliceStorageKey(key)))
  ]);
};

export interface Context extends CompanyStateI {
  setLocations: (locations: any[]) => void;
  setApplicantsActivity: (activity: any[]) => void;
  setUsers: (users: any[]) => void;
  setClients: (clients: any[]) => void;
  setCarePartners: (carePartners: any[]) => void;
  setCompanyTypes: (companyTypes: any[]) => void;
  resetCompany: () => void;
  setactivityTypes: (activityTypes: any[]) => void;
  setRatings: (ratings: any[]) => void;
  setCompanyRatings: (ratings: any[]) => void;
  setPriorityIndexes: (ratings: any[]) => void;
  setPriorityIndexesCP: (ratings: any[]) => void;
  setCareManagerActivities: (activities: any[]) => void;
  setApplicants: (applicants: any[]) => void;
  setAwardTypes: (awardTypes: any[]) => void;
  setDeactivationReasons: (reasons: any[]) => void;
  setLostClientReasons: (reasons: any[]) => void;
  setProspectlostClientReason: (reasons: any[]) => void;
  setApplicantStages: (reasons: any[]) => void;
  setReferralPartners: (referralPartners: any[]) => void;
  setProspect: (prospect: any[]) => void;
  setMilestones: (prospect: any[]) => void;
  setApplicantSources: (sources: any[]) => void;
  setLeadSources: (leadSources: any[]) => void;
  setReferralActivityTypes: (referralActivityTypes: any[]) => void;
  setApplicantActivityType: (data: any[]) => void;
  setApplicantActivityTypeCategory: (ratings: any[]) => void;
  setActivityCategoryManagement: (ratings: any[]) => void;
  setReferralPartnersActivityCategory: (ratings: any[]) => void;
  setProspectActivityCategory: (ratings: any[]) => void;
  setAuthorizationTypes: (data: any[]) => void;
  setAuthorizationStages: (prospect: any[]) => void;
  setReAuthorizationStages: (data: any[]) => void;
  setAuthorizations: (data: any[]) => void;
  setRequestedStages: (data: any[]) => void;
  setTags: (data: any[]) => void;
  setTagCategory: (data: any[]) => void;
  setprospectStages: (data: any[]) => void;
  setContactTypes: (data: any[]) => void;
  setContactRelationships: (data: any[]) => void;
  setDisciplinarySteps: (data: any[]) => void;
  setDisciplinaryCategories: (data: any[]) => void;
  setDisciplinaryAcks: (data: any[]) => void;
  setDisciplinaryAttendanceTypes: (data: any[]) => void;
  setApplicantThreshold: (data: any[]) => void;
  refreshProspectStages: () => Promise<void>;
  refreshContactTypes: () => Promise<void>;
  refreshContactRelationships: () => Promise<void>;
  refreshDisciplinarySteps: () => Promise<void>;
  refreshDisciplinaryCategories: () => Promise<void>;
  refreshDisciplinaryAcks: () => Promise<void>;
  refreshDisciplinaryAttendanceTypes: () => Promise<void>;
  refreshApplicantThreshold: () => Promise<void>;
  refreshLocations: () => Promise<void>;
  refreshUsers: () => Promise<void>;
  refreshClients: () => Promise<void>;
  refreshCarePartners: () => Promise<void>;
  refreshCareManagerActivities: () => Promise<void>;
  refreshApplicants: () => Promise<void>;
  refreshAwardTypes: () => Promise<void>;
  refreshApplicantsActivity: () => Promise<void>;
  refreshDeactivationReasons: () => Promise<void>;
  refreshLostClientReasons: () => Promise<void>;
  refreshProspectlostClientReason: () => Promise<void>;
  refreshApplicantStages: () => Promise<void>;
  refreshReferralPartners: () => Promise<void>;
  refreshCompanies: () => Promise<void>;
  refreshProspect: () => Promise<void>;
  refreshAll: () => Promise<void>;
  refreshFew: () => Promise<void>;
  setCompanies: (companies: any[]) => void;
  refreshMilestones: () => Promise<void>;
  refreshRatings: () => Promise<void>;
  refreshCompanyRatings: () => Promise<void>;
  refreshPriorityIndexes: () => Promise<void>;
  refreshPriorityIndexesCP: () => Promise<void>;
  refreshactivityTypes: () => Promise<void>;
  refreshCompanyTypes: () => Promise<void>;
  refreshApplicantSources: () => Promise<void>;
  refreshLeadSources: () => Promise<void>;
  refreshReferralActivityTypes: () => Promise<void>;
  refreshApplicantActivityType: () => Promise<void>;
  refreshApplicantActivityTypeCategory: () => Promise<void>;
  refreshActivityCategoryManagement: () => Promise<void>;
  refreshReferralPartnersActivityCategory: () => Promise<void>;
  refreshProspectActivityCategory: () => Promise<void>;
  refreshAuthorizationStages: () => Promise<void>;
  refreshAuthorizationTypes: () => Promise<void>;
  refreshReAuthorizationStages: () => Promise<void>;
  refreshAuthorizations: () => Promise<void>;
  refreshRequestedStages: () => Promise<void>;
  refreshTags: () => Promise<void>;
  refreshTagCategory: () => Promise<void>;
  invalidateCompanySlice: (sliceKey: CompanyStateKey, options?: { clearState?: boolean; refresh?: boolean }) => Promise<void>;
  patchCompanySlice: <T = any[]>(
    sliceKey: CompanyStateKey,
    updater: T | ((currentValue: T) => T)
  ) => T;
  refreshMap: Record<string, () => Promise<void>>;

}

export const CompanyContext = createContext({} as Context);
export const CompanyActionsContext = createContext({} as Omit<Context, keyof CompanyStateI>);

export const CompanyContextProvider: FC = ({ children }) => {
  const [state, dispatch] = useReducer(companyReducer, companyInitialState);
  const actionsRef = useRef<Omit<Context, keyof CompanyStateI>>();
  const hasHydratedRef = useRef(false);
  const previousPersistedStateRef = useRef(companyInitialState);
  const needsLegacyCleanupRef = useRef(false);
  const stateRef = useRef(state);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  useEffect(() => {
    let isMounted = true;

    const loadState = async () => {
      try {
        const storedSlices = await Promise.all(
          COMPANY_STATE_KEYS.map(async (key) => {
            const slice = await localForage.getItem<CompanyStateI[CompanyStateKey]>(getCompanySliceStorageKey(key));
            return [key, slice] as const;
          })
        );

        const hydratedSlices = storedSlices.reduce<Partial<CompanyStateI>>((acc, [key, slice]) => {
          if (slice !== null && slice !== undefined) {
            acc[key] = slice as CompanyStateI[CompanyStateKey];
          }
          return acc;
        }, {});

        const hasSplitCache = Object.keys(hydratedSlices).length > 0;

        if (isMounted && hasSplitCache) {
          const nextState = buildStateFromSlices(hydratedSlices);
          previousPersistedStateRef.current = nextState;
          dispatch({ type: CompanyActionTypes.LOAD_STATE, payload: nextState });
        } else if (isMounted) {
          const legacyStoredState = await localForage.getItem<Partial<CompanyStateI>>(LEGACY_STATE_LS_KEY);

          if (legacyStoredState && Object.keys(legacyStoredState).length > 0) {
            needsLegacyCleanupRef.current = true;
            previousPersistedStateRef.current = buildStateFromSlices(legacyStoredState);
            dispatch({ type: CompanyActionTypes.LOAD_STATE, payload: legacyStoredState });
          }
        }
      } catch (error) {
        logCompanyCacheError('hydrate company state', error);
      } finally {
        if (isMounted) {
          hasHydratedRef.current = true;
        }
      }
    };

    loadState();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!hasHydratedRef.current || !state || Object.keys(state).length === 0) {
      return;
    }

    const persistTimer = window.setTimeout(() => {
      const previousState = previousPersistedStateRef.current;
      const changedKeys = COMPANY_STATE_KEYS.filter((key) => previousState[key] !== state[key]);

      if (changedKeys.length === 0) {
        return;
      }

      const persistSlices = async () => {
        try {
          await Promise.all(
            changedKeys.map((key) => localForage.setItem(getCompanySliceStorageKey(key), state[key]))
          );

          if (needsLegacyCleanupRef.current) {
            await localForage.removeItem(LEGACY_STATE_LS_KEY);
            needsLegacyCleanupRef.current = false;
          }

          previousPersistedStateRef.current = state;
        } catch (error) {
          logCompanyCacheError('persist company state slices', error);
        }
      };

      persistSlices();
    }, PERSIST_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(persistTimer);
    };
  }, [state]);

  const setLocations = (locations: any[]) => dispatch({ type: CompanyActionTypes.SET_LOCATIONS, payload: locations });
  const setUsers = (users: any[]) => dispatch({ type: CompanyActionTypes.SET_USERS, payload: users });
  const setClients = (clients: any[]) => dispatch({ type: CompanyActionTypes.SET_CLIENTS, payload: clients });
  const setCarePartners = (carePartners: any[]) => dispatch({ type: CompanyActionTypes.SET_CAREPARTNERS, payload: carePartners });
  const setCareManagerActivities = (activities: any[]) => dispatch({ type: CompanyActionTypes.SET_CARE_MANAGER_ACTIVITIES, payload: activities });
  const setApplicants = (applicants: any[]) => dispatch({ type: CompanyActionTypes.SET_APPLICANTS, payload: applicants });
  const setAwardTypes = (awardTypes: any[]) => dispatch({ type: CompanyActionTypes.SET_AWARD_TYPES, payload: awardTypes });
  const setDeactivationReasons = (reasons: any[]) => dispatch({ type: CompanyActionTypes.SET_DEACTIVATION_REASONS, payload: reasons });
  const setLostClientReasons = (reasons: any[]) => dispatch({ type: CompanyActionTypes.SET_LOST_CLIENT_REASONS, payload: reasons });
  const setProspectlostClientReason = (reasons: any[]) => dispatch({ type: CompanyActionTypes.SET_PROSPECT_LOST_CLIENT_REASON, payload: reasons });
  const setApplicantStages = (reasons: any[]) => dispatch({ type: CompanyActionTypes.SET_APPLICANT_STAGES, payload: reasons });
  const setactivityTypes = (activityTypes: any[]) => dispatch({ type: CompanyActionTypes.SET_ACTIVITY_TYPES, payload: activityTypes });
  const setApplicantsActivity = (applicantsActivity: any[]) => dispatch({ type: CompanyActionTypes.SET_APPLICANT_ACTIVITY, payload: applicantsActivity });
  const setApplicantSources = (sources: any[]) => dispatch({ type: CompanyActionTypes.SET_APPLICANT_SOURCES, payload: sources });
  const setLeadSources = (leadSources: any[]) => dispatch({ type: CompanyActionTypes.SET_LEAD_SOURCES, payload: leadSources });
  const setReferralPartners = (referralPartners: any[]) => dispatch({ type: CompanyActionTypes.SET_REFERRAL_PARTNERS, payload: referralPartners });
  const setCompanies = (companies: any[]) => dispatch({ type: CompanyActionTypes.SET_COMPANIES, payload: companies });
  const setProspect = (prospect: any[]) => dispatch({ type: CompanyActionTypes.SET_PROSPECT, payload: prospect });
  const setprospectStages = (prospectStages: any[]) => dispatch({ type: CompanyActionTypes.SET_PROSPECT_STAGES, payload: prospectStages });
  const setMilestones = (milestones: any[]) => dispatch({ type: CompanyActionTypes.SET_MILESTONES, payload: milestones });
  const setRatings = (ratings: any[]) => dispatch({ type: CompanyActionTypes.SET_RATINGS, payload: ratings });
  const setCompanyRatings = (ratings: any[]) => dispatch({ type: CompanyActionTypes.SET_COMPANY_RATINGS, payload: ratings });
  const setPriorityIndexes = (ratings: any[]) => dispatch({ type: CompanyActionTypes.SET_PRIORITY_INDEXES, payload: ratings });
  const setPriorityIndexesCP = (ratings: any[]) => dispatch({ type: CompanyActionTypes.SET_PRIORITY_INDEXES_CP, payload: ratings });
  const setCompanyTypes = (companyTypes: any[]) => dispatch({ type: CompanyActionTypes.SET_COMPANY_TYPES, payload: companyTypes });
  const setReferralActivityTypes = (referralActivityTypes: any[]) => dispatch({ type: CompanyActionTypes.SET_REFERRAL_ACTIVITY_TYPES, payload: referralActivityTypes });
  const setApplicantActivityType = (data: any[]) => dispatch({ type: CompanyActionTypes.SET_APPLICANT_ACTIVITY_TYPE, payload: data });
  const setApplicantActivityTypeCategory = (data: any[]) => dispatch({ type: CompanyActionTypes.SET_APPLICANT_ACTIVITY_TYPE_CATEGORY, payload: data });
  const setActivityCategoryManagement = (data: any[]) => dispatch({ type: CompanyActionTypes.SET_ACTIVITY_CATEGORY_MANAGEMENT, payload: data });
  const setReferralPartnersActivityCategory = (data: any[]) => dispatch({ type: CompanyActionTypes.SET_REFERRAL_PARTNERS_ACTIVITY_CATEGORY, payload: data });
  const setProspectActivityCategory = (data: any[]) => dispatch({ type: CompanyActionTypes.SET_PROSPECT_ACTIVITY_CATEGORY, payload: data });
  const setAuthorizationStages = (data: any[]) => dispatch({ type: CompanyActionTypes.SET_AUTHORIZATION_STAGES, payload: data });
  const setReAuthorizationStages = (data: any[]) => dispatch({ type: CompanyActionTypes.SET_REAUTHORIZATION_STAGES, payload: data });
  const setAuthorizationTypes = (data: any[]) => dispatch({ type: CompanyActionTypes.SET_AUTHORIZATION_TYPES, payload: data });
  const setAuthorizations = (data: any[]) => dispatch({ type: CompanyActionTypes.SET_AUTHORIZATIONS, payload: data });
  const setRequestedStages = (data: any[]) => dispatch({ type: CompanyActionTypes.SET_REQUESTED_STAGES, payload: data });
  const setTags = (data: any[]) => dispatch({ type: CompanyActionTypes.SET_TAGS, payload: data });
  const setTagCategory = (data: any[]) => dispatch({ type: CompanyActionTypes.SET_TAG_CATEGORY, payload: data });
  const setContactTypes = (data: any[]) => dispatch({ type: CompanyActionTypes.SET_CONTACT_TYPES, payload: data });
  const setContactRelationships = (data: any[]) => dispatch({ type: CompanyActionTypes.SET_CONTACT_RELATIONSHIPS, payload: data });
  const setDisciplinarySteps = (data: any[]) => dispatch({ type: CompanyActionTypes.SET_DISCIPLINARY_STEPS, payload: data });
  const setDisciplinaryCategories = (data: any[]) => dispatch({ type: CompanyActionTypes.SET_DISCIPLINARY_CATEGORIES, payload: data });
  const setDisciplinaryAcks = (data: any[]) => dispatch({ type: CompanyActionTypes.SET_DISCIPLINARY_ACKS, payload: data });
  const setDisciplinaryAttendanceTypes = (data: any[]) => dispatch({ type: CompanyActionTypes.SET_DISCIPLINARY_ATTENDANCE_TYPES, payload: data });
  const setApplicantThreshold = (data: any[]) => dispatch({ type: CompanyActionTypes.SET_APPLICANT_THRESHOLD, payload: data });

  const sliceSetterMap: Record<CompanyStateKey, (data: any) => void> = {
    locations: setLocations,
    users: setUsers,
    clients: setClients,
    carePartners: setCarePartners,
    careManagerActivities: setCareManagerActivities,
    applicants: setApplicants,
    awardTypes: setAwardTypes,
    deactivationReasons: setDeactivationReasons,
    lostClientReasons: setLostClientReasons,
    prospectlostClientReason: setProspectlostClientReason,
    applicantStages: setApplicantStages,
    referralPartner: setReferralPartners,
    companies: setCompanies,
    prospect: setProspect,
    milestones: setMilestones,
    ratings: setRatings,
    companyRatings: setCompanyRatings,
    priorityIndexes: setPriorityIndexes,
    priorityIndexesCP: setPriorityIndexesCP,
    activityTypes: setactivityTypes,
    companyTypes: setCompanyTypes,
    applicantsActivity: setApplicantsActivity,
    applicantSources: setApplicantSources,
    leadSources: setLeadSources,
    referralActivityTypes: setReferralActivityTypes,
    applicantActivityTypeCategory: setApplicantActivityTypeCategory,
    activityCategoryManagement: setActivityCategoryManagement,
    prospectActivityCategory: setProspectActivityCategory,
    referralPartnersActivityCategory: setReferralPartnersActivityCategory,
    authorizationStages: setAuthorizationStages,
    reAuthorizationStages: setReAuthorizationStages,
    authorizationTypes: setAuthorizationTypes,
    authorizations: setAuthorizations,
    requestedStages: setRequestedStages,
    tags: setTags,
    tagCategory: setTagCategory,
    prospectStages: setprospectStages,
    contactTypes: setContactTypes,
    contactRelationships: setContactRelationships,
    disciplinarySteps: setDisciplinarySteps,
    disciplinaryCategories: setDisciplinaryCategories,
    disciplinaryAcks: setDisciplinaryAcks,
    disciplinaryAttendanceTypes: setDisciplinaryAttendanceTypes,
    applicantThreshold: setApplicantThreshold
  };

  const resetCompany = async () => {
    try {
      await removeCompanyCacheKeys();
    } catch (error) {
      logCompanyCacheError('clear company cache', error);
    }

    previousPersistedStateRef.current = companyInitialState;
    needsLegacyCleanupRef.current = false;
    dispatch({ type: CompanyActionTypes.RESET });
  };

  const createRefresher = (apiCall: () => Promise<any[]>, setter: (data: any[]) => void) => async () => {
    const data = await apiCall();
    setter(data);
  };

  const refreshLocations = createRefresher(async () => (await API.get('location/all?type=Active')).data.data, setLocations);
  const refreshUsers = createRefresher(async () => (await API.get('user/Active')).data.data, setUsers);
  const refreshClients = createRefresher(async () => (await API.get('client/ddn/0')).data.data, setClients);
  const refreshCarePartners = createRefresher(async () => (await API.get('care-partner/ddn/0')).data.data, setCarePartners);
  const refreshCareManagerActivities = createRefresher(async () => (await API.get('care-manager-activity')).data.data, setCareManagerActivities);
  const refreshApplicants = createRefresher(async () => (await API.get('applicants/ddn/0')).data.data, setApplicants);
  const refreshAwardTypes = createRefresher(async () => (await API.get('awards/ddn/Active')).data.data, setAwardTypes);
  const refreshDeactivationReasons = createRefresher(async () => (await API.get('client/activity/search/Active')).data.data, setDeactivationReasons);
  const refreshLostClientReasons = createRefresher(async () => (await API.get('care-partner/activity/search/Active')).data.data, setLostClientReasons);
  const refreshProspectlostClientReason = createRefresher(async () => (await API.get('/lost-client/activity/search/Active')).data.data, setProspectlostClientReason);
  const refreshApplicantStages = createRefresher(async () => (await API.get('applicants/stages')).data.data, setApplicantStages);
  const refreshReferralPartners = createRefresher(async () => (await API.get('referral-partners/ddn/0')).data.data, setReferralPartners);
  const refreshCompanies = createRefresher(async () => (await API.get('referral-partners/companies/ddn/0')).data.data, setCompanies);
  const refreshProspect = createRefresher(async () => (await API.get('/prospects/ddn/all')).data.data, setProspect);
  const refreshMilestones = createRefresher(async () => (await API.get('/prospects/progress')).data.data, setMilestones);
  const refreshRatings = createRefresher(async () => (await API.get('/rating-referral/activity/search/Active?referral=true')).data.data, setRatings);
  const refreshCompanyRatings = createRefresher(async () => (await API.get('/rating-referral-company/activity/search/Active?referral=true')).data.data, setCompanyRatings);
  const refreshPriorityIndexes = createRefresher(async () => (await API.get('/priority-index/activity/search/Active')).data.data, setPriorityIndexes);
  const refreshPriorityIndexesCP = createRefresher(async () => (await API.get('/priority-index-cp/activity/search/Active')).data.data, setPriorityIndexesCP);
  const refreshactivityTypes = createRefresher(async () => (await API.get(`marketing/types?referral=false`)).data.data, setactivityTypes);
  const refreshCompanyTypes = createRefresher(async () => (await API.get(`referral-partners/types/listing`)).data.data, setCompanyTypes);
  const refreshApplicantsActivity = createRefresher(async () => (await API.get('applicants/activity')).data.data, setApplicantsActivity);
  const refreshApplicantSources = createRefresher(async () => (await API.get('applicants/sources')).data.data, setApplicantSources);
  const refreshLeadSources = createRefresher(async () => (await API.get('/lead-source/activity/search/Active')).data.data, setLeadSources);
  const refreshReferralActivityTypes = createRefresher(async () => (await API.get(`marketing/types?referral=true`)).data.data, setReferralActivityTypes);
  const refreshApplicantActivityType = createRefresher(async () => (await API.get('/applicant-activity-type/activity/search/Active?referral=true')).data.data, setApplicantActivityType);
  const refreshApplicantActivityTypeCategory = createRefresher(async () => (await API.get('/applicant-activity-type-category/activity/search/Active?referral=true')).data.data, setApplicantActivityTypeCategory);
  const refreshActivityCategoryManagement = createRefresher(async () => (await API.get('/activity-category-management/activity/search/Active?referral=true')).data.data, setActivityCategoryManagement);
  const refreshReferralPartnersActivityCategory = createRefresher(async () => (await API.get('/marketing-activity-type-category/activity/search/Active?referral=true')).data.data, setReferralPartnersActivityCategory);
  const refreshProspectActivityCategory = createRefresher(async () => (await API.get('/marketing-activity-type-category/activity/search/Active?referral=false')).data.data, setProspectActivityCategory);
  const refreshAuthorizationStages = createRefresher(async () => (await API.get('/authorization/progress')).data.data, setAuthorizationStages);
  const refreshReAuthorizationStages = createRefresher(async () => (await API.get('/reauthorization/progress')).data.data, setReAuthorizationStages);
  const refreshRequestedStages = createRefresher(async () => (await API.get('/requested/stages')).data.data, setRequestedStages);
  const refreshAuthorizationTypes = createRefresher(async () => (await API.get('/authorization-types/activity/search/Active?referral=true')).data.data, setAuthorizationTypes);
  const refreshAuthorizations = createRefresher(async () => (await API.get('authorization/ddn/all')).data.data, setAuthorizations);
  const refreshTags = createRefresher(async () => (await API.get('tags/ddn/Active')).data.data, setTags);
  const refreshTagCategory = createRefresher(async () => (await API.get('tag-category/activity/search/Active')).data.data, setTagCategory);
  const refreshProspectStages = createRefresher(async () => (await API.get('/prospects/stages')).data.data, setprospectStages);
  const refreshContactTypes = createRefresher(async () => (await API.get('/contact-type/activity/search/Active')).data.data, setContactTypes);
  const refreshContactRelationships = createRefresher(async () => (await API.get('/contact-relationships/activity/search/Active')).data.data, setContactRelationships);
  const refreshDisciplinarySteps = createRefresher(async () => (await API.get('/disciplinary-step/activity/search/Active')).data.data, setDisciplinarySteps);
  const refreshDisciplinaryCategories = createRefresher(async () => (await API.get('/disciplinary-category/activity/search/Active')).data.data, setDisciplinaryCategories);
  const refreshDisciplinaryAcks = createRefresher(async () => (await API.get('/disciplinary-ack/activity/search/Active')).data.data, setDisciplinaryAcks);
  const refreshDisciplinaryAttendanceTypes = createRefresher(async () => (await API.get('/disciplinary-attendance-type/activity/search/Active')).data.data, setDisciplinaryAttendanceTypes);
  const refreshApplicantThreshold = createRefresher(async () => (await API.get('/applicant-threshold/activity/search/Active')).data.data, setApplicantThreshold);
  const refreshSliceMap: Partial<Record<CompanyStateKey, () => Promise<void>>> = {
    locations: refreshLocations,
    users: refreshUsers,
    clients: refreshClients,
    carePartners: refreshCarePartners,
    careManagerActivities: refreshCareManagerActivities,
    applicants: refreshApplicants,
    awardTypes: refreshAwardTypes,
    deactivationReasons: refreshDeactivationReasons,
    lostClientReasons: refreshLostClientReasons,
    prospectlostClientReason: refreshProspectlostClientReason,
    applicantStages: refreshApplicantStages,
    referralPartner: refreshReferralPartners,
    companies: refreshCompanies,
    prospect: refreshProspect,
    milestones: refreshMilestones,
    ratings: refreshRatings,
    companyRatings: refreshCompanyRatings,
    priorityIndexes: refreshPriorityIndexes,
    priorityIndexesCP: refreshPriorityIndexesCP,
    activityTypes: refreshactivityTypes,
    companyTypes: refreshCompanyTypes,
    applicantsActivity: refreshApplicantsActivity,
    applicantSources: refreshApplicantSources,
    leadSources: refreshLeadSources,
    referralActivityTypes: refreshReferralActivityTypes,
    applicantActivityTypeCategory: refreshApplicantActivityTypeCategory,
    activityCategoryManagement: refreshActivityCategoryManagement,
    prospectActivityCategory: refreshProspectActivityCategory,
    referralPartnersActivityCategory: refreshReferralPartnersActivityCategory,
    authorizationStages: refreshAuthorizationStages,
    reAuthorizationStages: refreshReAuthorizationStages,
    authorizationTypes: refreshAuthorizationTypes,
    authorizations: refreshAuthorizations,
    requestedStages: refreshRequestedStages,
    tags: refreshTags,
    tagCategory: refreshTagCategory
    ,
    contactTypes: refreshContactTypes,
    contactRelationships: refreshContactRelationships,
    disciplinarySteps: refreshDisciplinarySteps,
    disciplinaryCategories: refreshDisciplinaryCategories,
    disciplinaryAcks: refreshDisciplinaryAcks,
    disciplinaryAttendanceTypes: refreshDisciplinaryAttendanceTypes,
    applicantThreshold: refreshApplicantThreshold
  };

  const invalidateCompanySlice = async (
    sliceKey: CompanyStateKey,
    options: { clearState?: boolean; refresh?: boolean } = {}
  ) => {
    try {
      await localForage.removeItem(getCompanySliceStorageKey(sliceKey));

      if (options.clearState) {
        previousPersistedStateRef.current = {
          ...previousPersistedStateRef.current,
          [sliceKey]: companyInitialState[sliceKey]
        };
        sliceSetterMap[sliceKey](companyInitialState[sliceKey]);
      }

      if (options.refresh && refreshSliceMap[sliceKey]) {
        await refreshSliceMap[sliceKey]?.();
      }
    } catch (error) {
      logCompanyCacheError(`invalidate ${sliceKey} slice`, error);
    }
  };

  function patchCompanySlice<T = any[]>(
    sliceKey: CompanyStateKey,
    updater: T | ((currentValue: T) => T)
  ) {
    const currentValue = stateRef.current[sliceKey] as T;
    const nextValue =
      typeof updater === 'function'
        ? (updater as (currentValue: T) => T)(currentValue)
        : updater;

    sliceSetterMap[sliceKey](nextValue);
    return nextValue;
  }
  // Add this after refreshReferralActivityTypes
  const refreshMap: Record<string, () => Promise<void>> = {
    locations: refreshLocations,
    users: refreshUsers,
    clients: refreshClients,
    carePartners: refreshCarePartners,
    careManagerActivities: refreshCareManagerActivities,
    applicants: refreshApplicants,
    awardTypes: refreshAwardTypes,
    deactivationReasons: refreshDeactivationReasons,
    lostClientReasons: refreshLostClientReasons,
    prospectlostClientReason: refreshProspectlostClientReason,
    applicantStages: refreshApplicantStages,
    referralPartners: refreshReferralPartners,
    companies: refreshCompanies,
    prospects: refreshProspect,
    milestones: refreshMilestones,
    ratings: refreshRatings,
    companyRatings: refreshCompanyRatings,
    PriorityIndexes: refreshPriorityIndexes,
    PriorityIndexesCP: refreshPriorityIndexesCP,
    activityTypes: refreshactivityTypes,
    companyTypes: refreshCompanyTypes,
    applicantActivities: refreshApplicantsActivity,
    applicantSources: refreshApplicantSources,
    leadSources: refreshLeadSources,
    referralActivityTypes: refreshReferralActivityTypes,
    applicantActivityType: refreshApplicantActivityType,
    applicantActivityTypeCategory: refreshApplicantActivityTypeCategory,
    careManagerActivityCategory: refreshActivityCategoryManagement,
    referralPartnersActivityCategory: refreshReferralPartnersActivityCategory,
    prospectActivityCategory: refreshProspectActivityCategory,
    authorizationStages: refreshAuthorizationStages,
    authorizationTypes: refreshAuthorizationTypes,
    reAuthorizationStages: refreshReAuthorizationStages,
    authorizations: refreshAuthorizations,
    requestedStages: refreshRequestedStages,
    tagCategory: refreshTagCategory,
    tags: refreshTags,
    prospectStages: refreshProspectStages,
    contactTypes: refreshContactTypes,
    contactRelationships: refreshContactRelationships,
    disciplinarySteps: refreshDisciplinarySteps,
    disciplinaryCategories: refreshDisciplinaryCategories,
    disciplinaryAcks: refreshDisciplinaryAcks,
    disciplinaryAttendanceTypes: refreshDisciplinaryAttendanceTypes,
    applicantThreshold: refreshApplicantThreshold
  };

  const refreshAll = async () => {
    await Promise.all([
      refreshLocations(),
      refreshUsers(),
      refreshClients(),
      refreshCarePartners(),
      refreshCareManagerActivities(),
      refreshApplicants(),
      refreshAwardTypes(),
      refreshDeactivationReasons(),
      refreshLostClientReasons(),
      refreshProspectlostClientReason(),
      refreshApplicantStages(),
      refreshReferralPartners(),
      refreshCompanies(),
      refreshProspect(),
      refreshMilestones(),
      refreshRatings(),
      refreshCompanyRatings(),
      refreshPriorityIndexes(),
      refreshPriorityIndexesCP(),
      refreshactivityTypes(),
      refreshCompanyTypes(),
      refreshApplicantsActivity(),
      refreshApplicantSources(),
      refreshLeadSources(),
      refreshReferralActivityTypes(),
      refreshApplicantActivityTypeCategory(),
      refreshApplicantActivityType(),
      refreshActivityCategoryManagement(),
      refreshReferralPartnersActivityCategory(),
      refreshProspectActivityCategory(),
      refreshAuthorizationStages(),
      refreshReAuthorizationStages(),
      refreshAuthorizationTypes(),
      refreshAuthorizations(),
      refreshRequestedStages(),
      refreshTags(),
      refreshTagCategory(),
      refreshProspectStages(),
      refreshContactTypes(),
      refreshContactRelationships(),
      refreshDisciplinarySteps(),
      refreshDisciplinaryCategories(),
      refreshDisciplinaryAcks(),
      refreshDisciplinaryAttendanceTypes(),
      refreshApplicantThreshold()
    ]);
  };
  const refreshFew = async () => {
    await Promise.all([
      refreshClients(),
      refreshCarePartners(),
      refreshApplicants(),
      refreshReferralPartners(),
      refreshCompanies(),
      refreshProspect(),
    ]);
  };

  actionsRef.current = Object.assign(actionsRef.current ?? {}, {
      setLocations,
      setApplicantsActivity,
      setUsers,
      setClients,
      setCarePartners,
      setCompanyTypes,
      resetCompany,
      setactivityTypes,
      setRatings,
      setCompanyRatings,
      setPriorityIndexes,
      setPriorityIndexesCP,
      setCareManagerActivities,
      setApplicants,
      setAwardTypes,
      setDeactivationReasons,
      setLostClientReasons,
      setProspectlostClientReason,
      setApplicantStages,
      setReferralPartners,
      setProspect,
      setprospectStages,
      setMilestones,
      setApplicantSources,
      setLeadSources,
      setReferralActivityTypes,
      setApplicantActivityType,
      setApplicantActivityTypeCategory,
      setActivityCategoryManagement,
      setReferralPartnersActivityCategory,
      setProspectActivityCategory,
      setAuthorizationTypes,
      setAuthorizationStages,
      setReAuthorizationStages,
      setAuthorizations,
      setRequestedStages,
      setTags,
      setTagCategory,
      setContactTypes,
      setContactRelationships,
      setDisciplinarySteps,
      setDisciplinaryCategories,
      setDisciplinaryAcks,
      setDisciplinaryAttendanceTypes,
      setApplicantThreshold,
      refreshLocations,
      refreshUsers,
      refreshClients,
      refreshCarePartners,
      refreshCareManagerActivities,
      refreshApplicants,
      refreshAwardTypes,
      refreshApplicantsActivity,
      refreshDeactivationReasons,
      refreshLostClientReasons,
      refreshProspectlostClientReason,
      refreshApplicantStages,
      refreshReferralPartners,
      refreshCompanies,
      refreshProspect,
      refreshProspectStages,
      refreshAll,
      refreshFew,
      setCompanies,
      refreshMilestones,
      refreshRatings,
      refreshCompanyRatings,
      refreshPriorityIndexes,
      refreshPriorityIndexesCP,
      refreshactivityTypes,
      refreshCompanyTypes,
      refreshApplicantSources,
      refreshLeadSources,
      refreshReferralActivityTypes,
      refreshApplicantActivityType,
      refreshApplicantActivityTypeCategory,
      refreshActivityCategoryManagement,
      refreshReferralPartnersActivityCategory,
      refreshProspectActivityCategory,
      refreshAuthorizationStages,
      refreshAuthorizationTypes,
      refreshReAuthorizationStages,
      refreshAuthorizations,
      refreshRequestedStages,
      refreshTags,
      refreshTagCategory,
      refreshContactTypes,
      refreshContactRelationships,
      refreshDisciplinarySteps,
      refreshDisciplinaryCategories,
      refreshDisciplinaryAcks,
      refreshDisciplinaryAttendanceTypes,
      refreshApplicantThreshold,
      invalidateCompanySlice,
      patchCompanySlice,
      refreshMap
    });

  const actions = actionsRef.current;

  return (
    <CompanyActionsContext.Provider value={actions}>
      <CompanyContext.Provider value={{ ...state, ...actions }}>
        {children}
      </CompanyContext.Provider>
    </CompanyActionsContext.Provider>
  );
};

export default function useCompany() {
  return useContext(CompanyContext);
}

export function useCompanyActions() {
  return useContext(CompanyActionsContext);
}
